import { hexToString, isAddress } from 'viem'
import {
  createMulticallRetryUniversal,
  LIST_OVERRIDES,
} from '@1delta/providers'

/**
 * ERC-20 metadata, read straight off the contract.
 *
 * Two ABIs, not one. `name()` and `symbol()` are `string` in the standard, but
 * a long tail of pre-standard tokens (MKR, SAI, and most tokens deployed before
 * mid-2017) return a `bytes32` instead. Decoding one with the other's ABI does
 * not return a wrong answer — it throws — so the bytes32 form is a SECOND pass
 * over whatever the first pass could not decode, never a guess applied blindly.
 */
const ERC20_STRING_ABI = [
  {
    inputs: [],
    name: 'name',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const ERC20_BYTES32_ABI = [
  {
    inputs: [],
    name: 'name',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

export type TokenEntry = {
  chainId: string
  decimals: number
  name: string
  address: string
  symbol: string
  assetGroup: string
  currencyId: string
}

export interface FetchTokenMetadataOptions {
  /**
   * RPCs to read through. Defaults to the curated per-chain overrides — pass
   * an explicit list from a Worker, where `LIST_OVERRIDES` is not necessarily
   * the set the deployment is allowed to use.
   */
  rpcUrls?: string[]
  maxRetries?: number
}

/** `'0x'` is the failure sentinel the multicall layer substitutes for a reverted call. */
const FAILED = '0x'

function decodeBytes32(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.startsWith('0x')) return undefined
  if (value === FAILED) return undefined
  try {
    // `hexToString` keeps the zero padding; a bytes32 name is NUL-terminated.
    const decoded = hexToString(value as `0x${string}`)
      .replace(/\0+$/, '')
      .trim()
    return decoded.length > 0 ? decoded : undefined
  } catch {
    return undefined
  }
}

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  if (value === FAILED) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Read `name`/`symbol`/`decimals` for arbitrary addresses on one chain.
 *
 * Three properties this function is responsible for, all of which the earlier
 * `allowFailure: false` version got wrong:
 *
 * 1. **One bad address must not sink the batch.** A caller resolving a pasted
 *    address alongside five known ones would otherwise get nothing back.
 * 2. **A missing `decimals()` means NOT AN ERC-20, and the address is
 *    OMITTED.** It is never defaulted to 18 — every amount in this codebase is
 *    scaled by that number, so a wrong decimals is a wrong transaction, and a
 *    silent default is the shape that produces one.
 * 3. **`name`/`symbol` are cosmetic and may legitimately be absent.** A token
 *    with real `decimals` and no `symbol` still resolves; the strings fall back
 *    to the address prefix rather than dropping the row.
 *
 * @returns entries keyed by LOWERCASED address. Addresses that did not resolve
 *   are absent from the result — the caller must not assume input order or
 *   completeness.
 */
export async function fetchTokenMetadata(
  chain: string,
  addrs: string[],
  options: FetchTokenMetadataOptions = {},
): Promise<Record<string, TokenEntry>> {
  const { rpcUrls, maxRetries = 3 } = options

  const unique = [
    ...new Set(
      addrs
        .filter((a) => typeof a === 'string' && isAddress(a, { strict: false }))
        .map((a) => a.toLowerCase()),
    ),
  ]
  if (unique.length === 0) return {}

  const multicall = createMulticallRetryUniversal(
    rpcUrls?.length ? { [chain]: rpcUrls } : LIST_OVERRIDES,
  )

  const calls = unique.flatMap((address) => [
    { address, name: 'name', args: [] },
    { address, name: 'symbol', args: [] },
    { address, name: 'decimals', args: [] },
  ])

  const results = (await multicall({
    chain,
    calls,
    abi: ERC20_STRING_ABI,
    maxRetries,
    allowFailure: true,
  })) as unknown[]

  const names = new Map<string, string | undefined>()
  const symbols = new Map<string, string | undefined>()
  const decimalsByAddr = new Map<string, number | undefined>()

  unique.forEach((address, i) => {
    names.set(address, asString(results[i * 3]))
    symbols.set(address, asString(results[i * 3 + 1]))
    const raw = results[i * 3 + 2]
    decimalsByAddr.set(
      address,
      typeof raw === 'number'
        ? raw
        : typeof raw === 'bigint'
          ? Number(raw)
          : undefined,
    )
  })

  // Second pass: bytes32 name/symbol for the pre-standard tokens. Only the
  // addresses that have real `decimals` are worth a round trip — anything
  // without it is dropped regardless of what its strings say.
  const needsBytes32 = unique.filter(
    (a) =>
      decimalsByAddr.get(a) !== undefined &&
      (names.get(a) === undefined || symbols.get(a) === undefined),
  )

  if (needsBytes32.length > 0) {
    const b32Calls = needsBytes32.flatMap((address) => [
      { address, name: 'name', args: [] },
      { address, name: 'symbol', args: [] },
    ])
    try {
      const b32 = (await multicall({
        chain,
        calls: b32Calls,
        abi: ERC20_BYTES32_ABI,
        maxRetries,
        allowFailure: true,
      })) as unknown[]

      needsBytes32.forEach((address, i) => {
        if (names.get(address) === undefined) {
          const decoded = decodeBytes32(b32[i * 2])
          if (decoded) names.set(address, decoded)
        }
        if (symbols.get(address) === undefined) {
          const decoded = decodeBytes32(b32[i * 2 + 1])
          if (decoded) symbols.set(address, decoded)
        }
      })
    } catch {
      // The string pass already carries everything mandatory; a failed
      // cosmetic retry must not fail the whole resolution.
    }
  }

  const tokens: Record<string, TokenEntry> = {}

  for (const address of unique) {
    const decimals = decimalsByAddr.get(address)
    // NOT an ERC-20 (an EOA, an NFT, a proxy to nothing). Dropping it is the
    // whole point — see property 2 above.
    if (decimals === undefined || !Number.isInteger(decimals)) continue
    if (decimals < 0 || decimals > 36) continue

    const symbol = symbols.get(address) ?? `${address.slice(0, 6)}…`
    const name = names.get(address) ?? symbol

    tokens[address] = {
      chainId: String(chain),
      decimals,
      name,
      address,
      symbol,
      assetGroup: `${symbol}::${name}`,
      currencyId: `${symbol}::${name}`,
    }
  }

  return tokens
}
